import React, { useState, useEffect, useCallback } from "react";
import { BASE_API_URL, API_KEY } from "./config";

const REALMS = ["Animals","Birds","Plants","People","Places","Science","History","Sports","Food","Programming","Other"];
const HABITATS = {
  Animals: ["Jungle","Ocean","Land","Sky","Desert","River","Mountain","Country","Forest","Farm"],
  Birds:   ["Sky","Forest","Ocean","Land","Wetland","Desert"],
  Plants:  ["Forest","Garden","Desert","Wetland","Ocean","Farm"],
  People:  ["Country","City","Village","History"],
  Places:  ["Country","City","Ocean","Mountain","Desert","Forest"],
  Science: ["Space","Lab","Nature","Technology"],
  History: ["Ancient","Medieval","Modern","Country"],
  Sports:  ["Field","Court","Pool","Track","Indoor"],
  Food:    ["Fruit","Vegetable","Grain","Seafood","Dairy","Spice"],
  Programming: ["C","Python","Java","Data Structures","Algorithms","Web","Concepts"],
  Other:   ["General"],
};

const S = {
  page:   { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a2e1a)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"16px 14px 80px" },
  card:   { background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  title:  { fontSize:20, fontWeight:800, color:"#fff", marginBottom:4 },
  sub:    { fontSize:12, color:"#94a3b8", marginBottom:16 },
  label:  { fontSize:11, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.5px", display:"block", marginBottom:5 },
  input:  { width:"100%", boxSizing:"border-box", background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.15)", borderRadius:8, padding:"9px 12px", color:"#fff", fontSize:14, outline:"none" },
  select: { width:"100%", boxSizing:"border-box", background:"#1e293b", border:"1px solid rgba(255,255,255,0.15)", borderRadius:8, padding:"9px 12px", color:"#fff", fontSize:14, outline:"none" },
  btn:    (c) => ({ background:c, color:"#fff", border:"none", borderRadius:9, padding:"9px 18px", fontWeight:700, fontSize:13, cursor:"pointer" }),
  grid2:  { display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 },
  tag:    (c) => ({ background:c, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700 }),
  chip:   (active) => ({
    background: active ? "#16a34a" : "rgba(255,255,255,0.09)",
    color: active ? "#fff" : "#94a3b8",
    borderRadius:20, padding:"3px 12px", fontSize:12, fontWeight: active ? 700 : 400, cursor:"pointer", border:"none",
  }),
};

const EMPTY = { note_date:"", note_time:"08:00", realm:"Animals", habitat:"Jungle", topic:"", topic_tamil:"", content_eng:"", content_tamil:"", question_text:"", question_text_tamil:"", option_a:"", option_b:"", option_c:"", option_d:"", correct_option:"", image_url:"", added_by:"Teacher" };

// ── VideoModal ────────────────────────────────────────────────────────────────
function VideoModal({ noteId, noteTopic, onClose }) {
  const [lang,     setLang]     = useState("ta");
  const [jobId,    setJobId]    = useState(null);
  const [status,   setStatus]   = useState(null); // { status, progress, elapsed, file, error }
  const [starting, setStarting] = useState(false);
  const headers = { "Content-Type":"application/json", "X-API-Key": API_KEY };

  // Poll for status every 3 seconds while running
  useEffect(() => {
    if (!jobId) return;
    if (status?.status === "done" || status?.status === "error") return;
    const timer = setInterval(async () => {
      try {
        const r = await fetch(`${BASE_API_URL}/readingnotes/video/status?jobId=${jobId}`, { headers });
        const d = await r.json();
        setStatus(d);
      } catch(e) { /* ignore */ }
    }, 3000);
    return () => clearInterval(timer);
  }, [jobId, status]);

  const handleStart = async () => {
    setStarting(true);
    setStatus(null);
    try {
      const r = await fetch(`${BASE_API_URL}/readingnotes/video?id=${noteId}&lang=${lang}`, {
        method:"POST", headers,
      });
      const d = await r.json();
      if (d.jobId) {
        setJobId(d.jobId);
        setStatus({ status:"running", progress:"Starting..." });
      } else {
        setStatus({ status:"error", error: d.error || "Failed to start" });
      }
    } catch(e) {
      setStatus({ status:"error", error: e.message });
    }
    setStarting(false);
  };

  const downloadURL = jobId
    ? `${BASE_API_URL}/readingnotes/video/download?jobId=${jobId}`
    : null;

  const isDone  = status?.status === "done";
  const isError = status?.status === "error";
  const isRunning = status?.status === "running";

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
      <div style={{ background:"#0f172a", border:"1px solid rgba(255,255,255,0.15)", borderRadius:18, padding:24, maxWidth:400, width:"100%" }}>
        <div style={{ fontWeight:800, fontSize:17, color:"#fff", marginBottom:4 }}>🎬 Generate Video</div>
        <div style={{ fontSize:13, color:"#94a3b8", marginBottom:18 }}>
          Topic: <b style={{ color:"#fbbf24" }}>{noteTopic}</b> (Note #{noteId})
        </div>

        {/* Language selector */}
        {!jobId && (
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:12, fontWeight:700, color:"#94a3b8", display:"block", marginBottom:6 }}>LANGUAGE</label>
            <div style={{ display:"flex", gap:10 }}>
              {[["ta","Tamil"], ["en","English"], ["both","Tamil + English"]].map(([v, label]) => (
                <button key={v} onClick={() => setLang(v)}
                  style={{ flex:1, padding:"8px 4px", borderRadius:8, border:"none", fontSize:12, fontWeight:700, cursor:"pointer",
                    background: lang===v ? "#7c3aed" : "rgba(255,255,255,0.08)",
                    color: lang===v ? "#fff" : "#94a3b8" }}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Start button */}
        {!jobId && (
          <button onClick={handleStart} disabled={starting}
            style={{ width:"100%", padding:"11px", borderRadius:10, border:"none", background:"#7c3aed", color:"#fff", fontWeight:800, fontSize:14, cursor:"pointer", marginBottom:14 }}>
            {starting ? "Starting…" : "▶ Generate MP4"}
          </button>
        )}

        {/* Status */}
        {status && (
          <div style={{ background:"rgba(255,255,255,0.05)", borderRadius:10, padding:"14px 16px", marginBottom:14 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
              <span style={{ fontSize:20 }}>{isDone ? "✅" : isError ? "❌" : "⏳"}</span>
              <span style={{ fontWeight:700, color: isDone ? "#4ade80" : isError ? "#f87171" : "#fbbf24", fontSize:14 }}>
                {isDone ? "Done!" : isError ? "Failed" : "Generating…"}
              </span>
              {status.elapsed && <span style={{ fontSize:12, color:"#64748b" }}>{status.elapsed}</span>}
            </div>
            <div style={{ fontSize:13, color:"#cbd5e1" }}>{status.progress || status.error}</div>
            {isRunning && (
              <div style={{ marginTop:10, height:4, borderRadius:2, background:"rgba(255,255,255,0.1)", overflow:"hidden" }}>
                <div style={{ height:"100%", background:"#7c3aed", width:"60%", animation:"pulse 1.5s ease-in-out infinite" }} />
              </div>
            )}
          </div>
        )}

        {/* Download */}
        {isDone && downloadURL && (
          <a href={downloadURL} download
            style={{ display:"block", textAlign:"center", background:"#16a34a", color:"#fff", borderRadius:10, padding:"11px", fontWeight:800, fontSize:14, textDecoration:"none", marginBottom:12 }}>
            ⬇ Download MP4
          </a>
        )}

        {/* Regenerate after error */}
        {isError && (
          <button onClick={() => { setJobId(null); setStatus(null); }}
            style={{ width:"100%", padding:"9px", borderRadius:10, border:"none", background:"rgba(255,255,255,0.08)", color:"#fff", fontWeight:700, fontSize:13, cursor:"pointer", marginBottom:12 }}>
            🔄 Try Again
          </button>
        )}

        <button onClick={onClose}
          style={{ width:"100%", padding:"9px", borderRadius:10, border:"1px solid rgba(255,255,255,0.15)", background:"transparent", color:"#94a3b8", fontWeight:700, fontSize:13, cursor:"pointer" }}>
          Close
        </button>
      </div>
    </div>
  );
}

// ── Main Admin Page ───────────────────────────────────────────────────────────
export default function ReadingNotesAdmin() {
  const [notes, setNotes]         = useState([]);
  const [form,  setForm]          = useState(EMPTY);
  const [editID, setEditID]       = useState(null);
  const [filterTopic, setFilter]  = useState("");
  const [saving, setSaving]       = useState(false);
  const [msg, setMsg]             = useState("");
  const [videoNote, setVideoNote] = useState(null); // { id, topic } for modal

  const headers = { "Content-Type":"application/json", "X-API-Key": API_KEY };

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${BASE_API_URL}/readingnotes/archive?limit=200`);
      const d = await r.json();
      setNotes(Array.isArray(d) ? d : []);
    } catch(e) { console.error(e); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleChange = (field, val) => {
    setForm(f => {
      const next = { ...f, [field]: val };
      // reset habitat when realm changes
      if (field === "realm") {
        const hab = HABITATS[val]?.[0] || "Land";
        next.habitat = hab;
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!form.topic.trim() || !form.content_eng.trim()) { setMsg("⚠️ Topic and English content are required."); return; }
    setSaving(true); setMsg("");
    try {
      const url  = editID ? `${BASE_API_URL}/readingnotes/update` : `${BASE_API_URL}/readingnotes/add`;
      const body = editID ? { ...form, id: editID } : form;
      const r    = await fetch(url, { method:"POST", headers, body: JSON.stringify(body) });
      const d    = await r.json();
      if (d.status === "created" || d.status === "updated") {
        setMsg(editID ? "✅ Updated!" : "✅ Note added!");
        setForm(EMPTY); setEditID(null);
        load();
      } else {
        setMsg("❌ " + (d.error || "Save failed"));
      }
    } catch(e) { setMsg("❌ " + e.message); }
    setSaving(false);
  };

  const handleEdit = (note) => {
    setEditID(note.id);
    setForm({ ...EMPTY, ...note });
    window.scrollTo({ top:0, behavior:"smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    await fetch(`${BASE_API_URL}/readingnotes/delete?id=${id}`, { method:"DELETE", headers });
    load();
  };

  const handleCancel = () => { setForm(EMPTY); setEditID(null); setMsg(""); };

  const habitats   = HABITATS[form.realm] || ["Land"];
  const topics     = [...new Set(notes.map(n => n.topic))];
  const filtered   = filterTopic ? notes.filter(n => n.topic === filterTopic) : notes;

  return (
    <div style={S.page}>
      {videoNote && (
        <VideoModal
          noteId={videoNote.id}
          noteTopic={videoNote.topic}
          onClose={() => setVideoNote(null)}
        />
      )}
      <div style={S.title}>📝 Reading Notes Admin</div>
      <div style={S.sub}>Add / Edit daily reading notes for students</div>

      {/* ── Form ── */}
      <div style={S.card}>
        <div style={{ fontWeight:700, color:"#fff", marginBottom:14 }}>
          {editID ? `✏️ Edit Note #${editID}` : "➕ Add New Note"}
        </div>

        {/* Date / Time */}
        <div style={S.grid2}>
          <div>
            <label style={S.label}>Date</label>
            <input type="date" style={S.input} value={form.note_date}
              onChange={e => handleChange("note_date", e.target.value)} />
          </div>
          <div>
            <label style={S.label}>Time</label>
            <input type="time" style={S.input} value={form.note_time}
              onChange={e => handleChange("note_time", e.target.value)} />
          </div>
        </div>

        {/* Realm / Habitat hierarchy */}
        <div style={{ ...S.grid2, marginTop:12 }}>
          <div>
            <label style={S.label}>Realm (Category)</label>
            <select style={S.select} value={form.realm}
              onChange={e => handleChange("realm", e.target.value)}>
              {REALMS.map(r => <option key={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label style={S.label}>Habitat (Sub-category)</label>
            <select style={S.select} value={form.habitat}
              onChange={e => handleChange("habitat", e.target.value)}>
              {habitats.map(h => <option key={h}>{h}</option>)}
            </select>
          </div>
        </div>

        {/* Topic */}
        <div style={{ ...S.grid2, marginTop:12 }}>
          <div>
            <label style={S.label}>Topic (English)</label>
            <input style={S.input} placeholder="e.g. Lion" value={form.topic}
              onChange={e => handleChange("topic", e.target.value)} />
          </div>
          <div>
            <label style={S.label}>Topic (Tamil)</label>
            <input style={{ ...S.input, fontFamily:"'Noto Sans Tamil',sans-serif" }}
              placeholder="சிங்கம்" value={form.topic_tamil}
              onChange={e => handleChange("topic_tamil", e.target.value)} />
          </div>
        </div>

        {/* Content English */}
        <div style={{ marginTop:12 }}>
          <label style={S.label}>Content (English) — {form.content_eng.split(/\s+/).filter(Boolean).length} words</label>
          <textarea style={{ ...S.input, minHeight:120, resize:"vertical" }}
            placeholder="English reading content..."
            value={form.content_eng}
            onChange={e => handleChange("content_eng", e.target.value)} />
        </div>

        {/* Content Tamil */}
        <div style={{ marginTop:12 }}>
          <label style={S.label}>Content (Tamil) — {form.content_tamil.split(/\s+/).filter(Boolean).length} words</label>
          <textarea style={{ ...S.input, minHeight:100, resize:"vertical", fontFamily:"'Noto Sans Tamil',sans-serif", fontSize:16 }}
            placeholder="தமிழ் வாசிப்பு உள்ளடக்கம்..."
            value={form.content_tamil}
            onChange={e => handleChange("content_tamil", e.target.value)} />
        </div>

        {/* ── Comprehension MCQ (optional) ─────────────────────────────── */}
        <div style={{
          marginTop:18,
          padding:"14px 16px",
          background:"rgba(124,58,237,0.08)",
          border:"1px solid rgba(124,58,237,0.25)",
          borderRadius:10
        }}>
          <div style={{ fontSize:13, fontWeight:700, color:"#c4b5fd", marginBottom:10, letterSpacing:0.3 }}>
            📝 COMPREHENSION QUESTION (optional — used by image generator)
          </div>

          {/* Question text English / Tamil */}
          <div style={S.grid2}>
            <div>
              <label style={S.label}>Question (English)</label>
              <input style={S.input} placeholder="e.g. What is a group of lions called?"
                value={form.question_text}
                onChange={e => handleChange("question_text", e.target.value)} />
            </div>
            <div>
              <label style={S.label}>Question (Tamil)</label>
              <input style={{ ...S.input, fontFamily:"'Noto Sans Tamil',sans-serif" }}
                placeholder="சிங்கங்களின் கூட்டத்தை என்ன அழைக்கின்றனர்?"
                value={form.question_text_tamil}
                onChange={e => handleChange("question_text_tamil", e.target.value)} />
            </div>
          </div>

          {/* Options A & B */}
          <div style={{ ...S.grid2, marginTop:10 }}>
            <div>
              <label style={S.label}>Option A</label>
              <input style={S.input} placeholder="Pack" value={form.option_a}
                onChange={e => handleChange("option_a", e.target.value)} />
            </div>
            <div>
              <label style={S.label}>Option B</label>
              <input style={S.input} placeholder="Pride" value={form.option_b}
                onChange={e => handleChange("option_b", e.target.value)} />
            </div>
          </div>

          {/* Options C & D */}
          <div style={{ ...S.grid2, marginTop:10 }}>
            <div>
              <label style={S.label}>Option C</label>
              <input style={S.input} placeholder="Herd" value={form.option_c}
                onChange={e => handleChange("option_c", e.target.value)} />
            </div>
            <div>
              <label style={S.label}>Option D</label>
              <input style={S.input} placeholder="Flock" value={form.option_d}
                onChange={e => handleChange("option_d", e.target.value)} />
            </div>
          </div>

          {/* Correct option */}
          <div style={{ marginTop:10 }}>
            <label style={S.label}>Correct Option</label>
            <select
              style={{ ...S.input, maxWidth:140 }}
              value={form.correct_option}
              onChange={e => handleChange("correct_option", e.target.value)}>
              <option value="">— None —</option>
              <option value="A">A</option>
              <option value="B">B</option>
              <option value="C">C</option>
              <option value="D">D</option>
            </select>
            <div style={{ fontSize:11, color:"#64748b", marginTop:4 }}>
              Leave all MCQ fields blank for a passage-only note.
            </div>
          </div>
        </div>

        {/* Image URL / Added By */}
        <div style={{ ...S.grid2, marginTop:12 }}>
          <div>
            <label style={S.label}>Image URL (optional)</label>
            <input style={S.input} placeholder="https://..." value={form.image_url}
              onChange={e => handleChange("image_url", e.target.value)} />
            <div style={{ fontSize:11, color:"#64748b", marginTop:4 }}>
              Tip: images in /resources/ are auto-detected by topic name
            </div>
          </div>
          <div>
            <label style={S.label}>Added By</label>
            <input style={S.input} value={form.added_by}
              onChange={e => handleChange("added_by", e.target.value)} />
          </div>
        </div>

        {/* Buttons */}
        <div style={{ display:"flex", gap:10, marginTop:16, flexWrap:"wrap", alignItems:"center" }}>
          <button style={S.btn("#16a34a")} onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : editID ? "💾 Update Note" : "✅ Save Note"}
          </button>
          {editID && (
            <button style={S.btn("rgba(255,255,255,0.12)")} onClick={handleCancel}>Cancel</button>
          )}
          {msg && <span style={{ fontSize:13, color: msg.startsWith("✅") ? "#4ade80" : "#f87171" }}>{msg}</span>}
        </div>
      </div>

      {/* ── Topic filter chips ── */}
      {topics.length > 0 && (
        <div style={S.card}>
          <div style={{ fontSize:13, fontWeight:700, color:"#fff", marginBottom:10 }}>🏷️ Filter by Topic</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
            {topics.map((t, i) => (
              <button key={i} style={S.chip(filterTopic === t)}
                onClick={() => setFilter(filterTopic === t ? "" : t)}>{t}</button>
            ))}
          </div>
        </div>
      )}

      {/* ── Notes list ── */}
      <div style={S.card}>
        <div style={{ fontSize:15, fontWeight:700, color:"#fff", marginBottom:12 }}>
          📚 All Notes ({filtered.length})
        </div>
        {filtered.length === 0 && (
          <div style={{ color:"#64748b", textAlign:"center", padding:"16px 0" }}>No notes yet.</div>
        )}
        {filtered.map(n => (
          <div key={n.id} style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", marginBottom:10 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8 }}>
              <div style={{ flex:1 }}>
                {/* Hierarchy breadcrumb */}
                <div style={{ fontSize:11, color:"#64748b", marginBottom:4 }}>
                  <span style={S.tag("#1e3a5f")}>{n.realm}</span>
                  <span style={{ margin:"0 4px", color:"#475569" }}>›</span>
                  <span style={S.tag("#14532d")}>{n.habitat}</span>
                </div>
                <div style={{ fontWeight:700, fontSize:15, color:"#fff" }}>
                  {n.topic}
                  {n.topic_tamil && <span style={{ color:"#fbbf24", marginLeft:8, fontFamily:"'Noto Sans Tamil',sans-serif", fontSize:14 }}>{n.topic_tamil}</span>}
                </div>
                <div style={{ fontSize:12, color:"#64748b", marginTop:3 }}>
                  {n.note_date} · {n.note_time} · {n.added_by}
                </div>
              </div>
              {n.image_url && (
                <img src={n.image_url} alt={n.topic}
                  style={{ width:48, height:48, objectFit:"cover", borderRadius:8, flexShrink:0 }}
                  onError={e => { e.target.style.display="none"; }} />
              )}
            </div>
            <div style={{ display:"flex", gap:8, marginTop:10, flexWrap:"wrap" }}>
              <button style={{ ...S.btn("#1e40af"), padding:"5px 12px", fontSize:12 }} onClick={() => handleEdit(n)}>✏️ Edit</button>
              <button style={{ ...S.btn("#7f1d1d"), padding:"5px 12px", fontSize:12 }} onClick={() => handleDelete(n.id)}>🗑 Delete</button>
              <button style={{ ...S.btn("#7c3aed"), padding:"5px 12px", fontSize:12 }}
                onClick={() => {
                  const shareURL = "https://virtualkalvi.com/reading";
                  const text = `📖 ${n.topic_tamil || n.topic} — VirtualKalvi Daily Reading\n${shareURL}`;
                  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
                }}>💬 Share WA</button>
              <a href={`https://virtualkalvi.com/reading`} target="_blank" rel="noreferrer"
                style={{ ...S.btn("#0f766e"), padding:"5px 12px", fontSize:12, textDecoration:"none", display:"inline-flex", alignItems:"center" }}>
                👁 Preview
              </a>
              <button style={{ ...S.btn("#b45309"), padding:"5px 12px", fontSize:12 }}
                onClick={() => setVideoNote({ id: n.id, topic: n.topic_tamil || n.topic })}>
                🎬 Generate Video
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* ── YouTube Shorts / Video Guide ── */}
      <div style={{ ...S.card, borderColor:"rgba(255,0,0,0.3)", background:"rgba(255,0,0,0.05)" }}>
        <div style={{ fontSize:15, fontWeight:700, color:"#fff", marginBottom:10 }}>🎬 YouTube Shorts / Reels — Video Guide</div>
        <div style={{ fontSize:13, color:"#94a3b8", lineHeight:1.8 }}>
          To convert a reading note into a YouTube Short or Instagram Reel, follow these steps:<br/><br/>
          <b style={{ color:"#fff" }}>Step 1 — Download image</b><br/>
          Save the animal image (e.g. Lion.png) from <code style={{ background:"rgba(255,255,255,0.1)", borderRadius:4, padding:"1px 6px" }}>/resources/</code> to your phone.<br/><br/>
          <b style={{ color:"#fff" }}>Step 2 — Record reading</b><br/>
          Open <a href="https://virtualkalvi.com/reading" target="_blank" rel="noreferrer" style={{ color:"#60a5fa" }}>virtualkalvi.com/reading</a>, navigate to the note, tap 🔊 Read Aloud, then <b>screen-record</b> your phone while it reads.<br/><br/>
          <b style={{ color:"#fff" }}>Step 3 — Edit with CapCut / InShot</b><br/>
          Import the screen recording into CapCut (free app). Crop to 9:16 (Shorts ratio). Add the VirtualKalvi logo watermark, music, and captions. Export and upload.<br/><br/>
          <b style={{ color:"#4ade80" }}>Step 4 — Auto-video ✅ LIVE!</b><br/>
          Click <b style={{ color:"#fbbf24" }}>🎬 Generate Video</b> on any note below. Our server uses <b>ffmpeg + Puppeteer</b> to auto-generate a 9:16 MP4 Short with image slides, Tamil/English TTS audio, per-point text, and VirtualKalvi branding. Download and upload directly to YouTube Shorts or Instagram Reels!
        </div>
        <div style={{ display:"flex", gap:10, marginTop:14, flexWrap:"wrap" }}>
          <a href="https://www.capcut.com" target="_blank" rel="noreferrer"
            style={{ ...S.btn("#dc2626"), textDecoration:"none", fontSize:12, padding:"6px 14px" }}>
            🎬 Open CapCut
          </a>
          <a href="https://virtualkalvi.com/reading" target="_blank" rel="noreferrer"
            style={{ ...S.btn("#16a34a"), textDecoration:"none", fontSize:12, padding:"6px 14px" }}>
            📖 Open Reading Page
          </a>
        </div>
      </div>
    </div>
  );
}
